import React, { useContext, useState } from "react";
import styled from "styled-components";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserContext } from "../context/UserContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Code2, CloudUpload, Sparkles } from "lucide-react";

const PostCodeIssue = () => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    code: "",
    technology: "",
    difficulty: "",
    type: "free",
    reward: "",
    expected: "",
    actual: "",
    errorLogs: "",
    steps: "",
    environment: "",
    triedSolutions: "",
  });
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);

  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const storage = getStorage();

  const technologies = [
    "React",
    "React Native",
    "Node.js",
    "Firebase",
    "Next.js",
    "GraphQL",
    "MongoDB",
    "Python",
    "Java",
    "Kotlin",
    "TypeScript",
    "Swift",
    "PHP",
    "Laravel",
    "MySQL",
    "PostgreSQL",
  ];

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) setScreenshot(file);
    else toast.error("Only image files are allowed!");
  };

  const generateAISolution = async (prompt) => {
    try {
      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: [{ role: "user", content: prompt }],
          }),
        }
      );

      const data = await response.json();
      return data.choices?.[0]?.message?.content || "No AI response.";
    } catch (err) {
      console.error("AI error:", err);
      return "AI generation failed.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let screenshotURL = null;
      if (screenshot) {
        const refPath = ref(
          storage,
          `screenshots/${Date.now()}-${screenshot.name}`
        );
        await uploadBytes(refPath, screenshot);
        screenshotURL = await getDownloadURL(refPath);
      }

      const issueRef = await addDoc(collection(db, "codeIssues"), {
        ...form,
        screenshot: screenshotURL,
        createdAt: Timestamp.now(),
        postedBy: user?.name || "Anonymous",
        posterId: user?.id,
        status: "UNSOLVED",
      });

      toast.info("💡 Generating AI solution...");

      const prompt = `
You are an expert ${form.technology} developer.
Analyze the issue and provide a short explanation and solution with code.

Title: ${form.title}
Description: ${form.description}
Code: ${form.code}
Expected: ${form.expected}
Actual: ${form.actual}
Error Logs: ${form.errorLogs}
Steps: ${form.steps}
Environment: ${form.environment}
Tried Solutions: ${form.triedSolutions}
`;

      const aiSolution = await generateAISolution(prompt);

      await addDoc(collection(db, "codeIssues", issueRef.id, "solutions"), {
        author: "AI Assistant",
        content: aiSolution,
        createdAt: Timestamp.now(),
        type: "ai",
        votes: 0,
      });

      toast.success("🚀 Issue posted successfully with AI solution!");
      navigate("/code-issues");
    } catch (err) {
      console.error(err);
      toast.error("Failed to post issue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      {loading && (
        <Overlay>
          <Loader />
          <LoadingText>AI is analyzing your issue...</LoadingText>
        </Overlay>
      )}

      <FormCard
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <Header>
          <Code2 size={30} color="#c59fff" />
          <h2>Post a Code Issue</h2>
        </Header>

        <Form onSubmit={handleSubmit}>
          <SectionTitle>🧩 Issue Details</SectionTitle>

          <InputGroup>
            <label>Title *</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Brief issue title"
              required
            />
          </InputGroup>

          <InputGroup>
            <label>Description *</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Explain your problem clearly..."
              required
            />
          </InputGroup>

          <InputGroup>
            <label>Code Snippet</label>
            <textarea
              className="code"
              name="code"
              value={form.code}
              onChange={handleChange}
              placeholder="Paste relevant code here..."
            />
          </InputGroup>

          <TwoColumns>
            <InputGroup>
              <label>Technology *</label>
              <select
                name="technology"
                value={form.technology}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {technologies.map((tech) => (
                  <option key={tech}>{tech}</option>
                ))}
              </select>
            </InputGroup>

            <InputGroup>
              <label>Difficulty *</label>
              <select
                name="difficulty"
                value={form.difficulty}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                <option>Easy</option>
                <option>Medium</option>
                <option>Hard</option>
              </select>
            </InputGroup>
          </TwoColumns>

          <TwoColumns>
            <InputGroup>
              <label>Type *</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                required
              >
                <option value="free">Free</option>
                <option value="paid">Paid</option>
              </select>
            </InputGroup>

            <InputGroup>
              <label>Reward (Optional)</label>
              <input
                name="reward"
                value={form.reward}
                onChange={handleChange}
                placeholder="Coins or money"
              />
            </InputGroup>
          </TwoColumns>

          <SectionTitle>⚙️ Debugging Context</SectionTitle>

          <InputGroup>
            <label>Expected Result</label>
            <textarea
              name="expected"
              value={form.expected}
              onChange={handleChange}
              placeholder="What should have happened?"
            />
          </InputGroup>

          <InputGroup>
            <label>Actual Result</label>
            <textarea
              name="actual"
              value={form.actual}
              onChange={handleChange}
              placeholder="What actually happened?"
            />
          </InputGroup>

          <InputGroup>
            <label>Error Logs</label>
            <textarea
              name="errorLogs"
              value={form.errorLogs}
              onChange={handleChange}
              placeholder="Paste relevant console or build logs"
            />
          </InputGroup>

          <InputGroup>
            <label>Steps to Reproduce</label>
            <textarea
              name="steps"
              value={form.steps}
              onChange={handleChange}
              placeholder="List steps to reproduce the issue"
            />
          </InputGroup>

          <InputGroup>
            <label>Environment Details</label>
            <textarea
              name="environment"
              value={form.environment}
              onChange={handleChange}
              placeholder="OS, versions, tools, dependencies"
            />
          </InputGroup>

          <InputGroup>
            <label>Tried Solutions</label>
            <textarea
              name="triedSolutions"
              value={form.triedSolutions}
              onChange={handleChange}
              placeholder="Mention fixes you already tried"
            />
          </InputGroup>

          <SectionTitle>📎 Attachment</SectionTitle>
          <FileUpload>
            <input type="file" accept="image/*" onChange={handleFileChange} />
            <CloudUpload size={20} />
            <span>{screenshot ? screenshot.name : "Upload screenshot"}</span>
          </FileUpload>

          <SubmitButton type="submit">
            <Sparkles size={18} /> Submit & Generate AI Fix
          </SubmitButton>
        </Form>
      </FormCard>
    </PageWrapper>
  );
};

export default PostCodeIssue;

// ---------------- Styled Components ----------------

const PageWrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  display: flex;
  justify-content: center;
  padding: 60px 20px;
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 40px;
  color: white;
  width: 100%;
  max-width: 750px;
  backdrop-filter: blur(8px);
  box-shadow: 0 0 25px rgba(155, 92, 255, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
  h2 {
    font-size: 1.8rem;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SectionTitle = styled.h3`
  color: #c59fff;
  margin-top: 30px;
  margin-bottom: 10px;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    color: #c59fff;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(155, 92, 255, 0.25);
    border-radius: 8px;
    color: white;
    padding: 12px;
    font-size: 0.95rem;
    &:focus {
      outline: none;
      border-color: #c59fff;
      box-shadow: 0 0 8px rgba(155, 92, 255, 0.3);
    }
  }

  textarea.code {
    font-family: "Fira Code", monospace;
    min-height: 160px;
  }
`;

const TwoColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  > div {
    flex: 1;
    min-width: 220px;
  }
`;

const FileUpload = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(155, 92, 255, 0.4);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  color: #c59fff;
  font-size: 0.9rem;
  transition: 0.3s;
  &:hover {
    background: rgba(155, 92, 255, 0.1);
  }
  input {
    display: none;
  }
`;

const SubmitButton = styled.button`
  margin-top: 30px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px 26px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 1rem;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.4);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const Loader = styled.div`
  border: 6px solid #333;
  border-top: 6px solid #c59fff;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin-top: 16px;
  color: #c59fff;
  font-weight: 500;
`;
